import * as React from 'react';
import type { GuestVoucherDto } from './GuestVoucherCard';

export interface VoucherDetailsPanelProps {
    voucher: GuestVoucherDto;
    isUsed: boolean;
    isReconcileDone: boolean;
    effectiveBaseUrl: string | null;
    bookingDateTime: string | null;
    coachName: string | null;
    locationName: string | null;
}

function formatDate(iso: string | null): string {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function formatDateFull(iso: string | null): string {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Mechanically extracted from VoucherActionModal to keep that file under the 500 LOC cap. No logic changes. */
export const VoucherDetailsPanel: React.FC<VoucherDetailsPanelProps> = ({
    voucher,
    isUsed,
    isReconcileDone,
    effectiveBaseUrl,
    bookingDateTime,
    coachName,
    locationName,
}) => {
    return (
        <div className="voucher-body">
            <div className="voucher-row">
                <span className="voucher-label">Guest</span>
                <span className="voucher-value">{voucher.personName || 'Unknown'}</span>
            </div>
            <div className="voucher-row">
                <span className="voucher-label">Voucher</span>
                <span className="voucher-value">V-{voucher.voucherNumber}</span>
            </div>
            <div className="voucher-row">
                <span className="voucher-label">Valid</span>
                <span className="voucher-value">
                    {formatDate(voucher.programStartDate ?? voucher.windowStart)} - {formatDateFull(voucher.programEndDate ?? voucher.windowEnd)}
                </span>
            </div>
            {voucher.voucherType === 'Red' && (
                <div className="voucher-row">
                    <span className="voucher-label">PT Validity</span>
                    <span className="voucher-value">{formatDate(voucher.windowStart)} - {formatDateFull(voucher.windowEnd)}</span>
                </div>
            )}
            <div className="voucher-row">
                <span className="voucher-label">Set</span>
                <span className="voucher-value">{voucher.setPosition} of {voucher.setTotal}</span>
            </div>

            {/* Redemption info for Used vouchers (on-site booking) */}
            {isUsed && voucher.redeemedServiceType && !voucher.externalRedemption && !isReconcileDone && (
                <div className="voucher-row" style={{ flexDirection: 'column', borderTop: '2px solid #e9ecef', paddingTop: '12px' }}>
                    <span className="voucher-label" style={{ marginBottom: '4px' }}>Redemption</span>
                    <span className="voucher-value" style={{ textAlign: 'left', color: '#059669' }}>
                        {voucher.redeemedServiceType}
                    </span>
                    {bookingDateTime && (
                        <div style={{ fontSize: '13px', color: '#374151', marginTop: '4px' }}>
                            <span style={{ color: '#6c757d' }}>Booking:</span> {bookingDateTime}
                        </div>
                    )}
                    {coachName && (
                        <div style={{ fontSize: '13px', color: '#374151', marginTop: '2px' }}>
                            <span style={{ color: '#6c757d' }}>Coach:</span> {coachName}
                        </div>
                    )}
                    {locationName && (
                        <div style={{ fontSize: '13px', color: '#374151', marginTop: '2px' }}>
                            <span style={{ color: '#6c757d' }}>Location:</span> {locationName}
                        </div>
                    )}
                    {/* Booking/Order links + Voucher URL */}
                    <div style={{ marginTop: '6px', fontSize: '13px' }}>
                        <a
                            href={`/v/${voucher.shortCode}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#3b82f6', textDecoration: 'none' }}
                        >
                            Voucher &#8599;
                        </a>
                        {effectiveBaseUrl && voucher.bookingId && (
                            <>
                                <span style={{ color: '#d1d5db', margin: '0 6px' }}>|</span>
                                <a
                                    href={`${effectiveBaseUrl}/people/${voucher.personId}/bookings/${voucher.bookingId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#3b82f6', textDecoration: 'none' }}
                                >
                                    Booking &#8599;
                                </a>
                            </>
                        )}
                        {effectiveBaseUrl && voucher.orderId && (
                            <>
                                <span style={{ color: '#d1d5db', margin: '0 6px' }}>|</span>
                                <a
                                    href={`${effectiveBaseUrl}/people/${voucher.personId}/orders/${voucher.orderId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#3b82f6', textDecoration: 'none' }}
                                >
                                    Order &#8599;
                                </a>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Already reconciled info (off-site massage) */}
            {isUsed && voucher.externalRedemption && !isReconcileDone && (
                <div className="voucher-row" style={{ flexDirection: 'column', borderTop: '2px solid #e9ecef', paddingTop: '12px' }}>
                    <span className="voucher-label" style={{ marginBottom: '4px', color: '#92400e' }}>Massage (off-site)</span>
                    <span className="voucher-value" style={{ textAlign: 'left' }}>
                        {voucher.externalMassageType} - {voucher.externalMassagePrice?.toLocaleString()} THB
                        {voucher.externalReceiptDate && (
                            <span style={{ color: '#6c757d' }}> (receipt {formatDateFull(voucher.externalReceiptDate)})</span>
                        )}
                    </span>
                </div>
            )}

            {/* Flag info */}
            {voucher.status === 'Flagged' && voucher.flagReason && (
                <div className="voucher-row" style={{ flexDirection: 'column', borderTop: '2px solid #e9ecef', paddingTop: '12px' }}>
                    <span className="voucher-label" style={{ marginBottom: '4px' }}>Flag Reason</span>
                    <span className="voucher-value" style={{ textAlign: 'left', color: '#1e40af' }}>
                        {voucher.flagReason}
                    </span>
                </div>
            )}
        </div>
    );
};
